import React, { useState } from 'react';
import {
  RocketLaunchIcon,
  SparklesIcon,
  ListBulletIcon,
  UserGroupIcon,
  ChartBarIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ClockIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export default function GettingStarted() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const features = [
    {
      icon: RocketLaunchIcon,
      title: 'Create Projects',
      description: 'Start by creating a new project with all essential details including title, description, budget, and timeline.',
      steps: [
        'Click "Add Project" button',
        'Fill in project details',
        'Upload business case (optional)',
        'AI analyzes and provides success probability',
        'Your project is ready!'
      ],
      color: 'blue'
    },
    {
      icon: SparklesIcon,
      title: 'AI Action Plans',
      description: 'Get intelligent, actionable guidance tailored to your project phase and context.',
      steps: [
        'Open any project',
        'Click "Show Action Plan" button',
        'Review 5 specific next actions',
        'See milestone roadmap',
        'Check critical path and risks',
        'Follow AI recommendations'
      ],
      color: 'purple'
    },
    {
      icon: ListBulletIcon,
      title: 'Task Management',
      description: 'Manage your project tasks with a beautiful Kanban board and drag-and-drop interface.',
      steps: [
        'Go to project → Tasks tab',
        'Click "AI Generate Tasks" for instant tasks',
        'Or click "Add Task" to create manually',
        'Drag tasks between columns',
        'Click task to edit details',
        'Track progress in real-time'
      ],
      color: 'green'
    },
    {
      icon: UserGroupIcon,
      title: 'Team Assignment',
      description: 'Assign tasks to team members and track who is working on what.',
      steps: [
        'Open or create a task',
        'Enter team member ID in "Assign To" field',
        'Task shows assignee badge',
        'Filter tasks by assignee',
        'Track team workload'
      ],
      color: 'orange'
    },
    {
      icon: ChartBarIcon,
      title: 'Progress Tracking',
      description: 'Monitor project health with real-time metrics and EVM dashboard.',
      steps: [
        'View dashboard for overview',
        'Check project health indicators',
        'Monitor budget utilization',
        'Track timeline progress',
        'Review EVM metrics'
      ],
      color: 'indigo'
    }
  ];

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I create my first project?',
          a: 'Click the "Add Project" button on the Projects page. Fill in the required fields (title, description, dates, budget) and optionally upload a business case document. Our AI will analyze your project and provide a success probability score.'
        },
        {
          q: 'What is the AI success probability?',
          a: 'When you create a project, our AI analyzes your business case, project details, and historical data to provide a probability score (0-100%) indicating the likelihood of project success. This helps you identify potential risks early.'
        },
        {
          q: 'Do I need to upload a business case?',
          a: 'No, the business case is optional. However, uploading one allows our AI to provide more accurate insights and recommendations based on your specific project context.'
        }
      ]
    },
    {
      category: 'AI Features',
      questions: [
        {
          q: 'How does AI Generate Tasks work?',
          a: 'Click "AI Generate Tasks" in the Tasks tab. Our AI (powered by Gemini 2.0) analyzes your project title, description, status, and priority to generate 5-8 specific, actionable tasks tailored to your project phase. Tasks are automatically categorized and prioritized.'
        },
        {
          q: 'What is the AI Action Plan?',
          a: 'The Action Plan provides phase-specific guidance including: 5 immediate next actions, milestone roadmap with timelines, critical path visualization, risk alerts, and strategic recommendations. It adapts based on whether you\'re in Planning, Execution, or Closing phase.'
        },
        {
          q: 'Can I edit AI-generated tasks?',
          a: 'Absolutely! All AI-generated tasks are fully editable. Click any task to modify title, description, priority, assignee, dates, or any other field. AI tasks are marked with metadata but function exactly like manually created tasks.'
        },
        {
          q: 'How often should I regenerate AI tasks?',
          a: 'You can generate tasks as often as needed. We recommend generating initial tasks when starting a project, then creating additional tasks manually as the project evolves. AI generation works best for kickstarting new projects or phases.'
        }
      ]
    },
    {
      category: 'Task Management',
      questions: [
        {
          q: 'What do the Kanban columns mean?',
          a: 'The 5 columns represent task status: To Do (not started), In Progress (actively working), In Review (awaiting approval), Blocked (cannot proceed), and Completed (finished). Drag tasks between columns to update their status.'
        },
        {
          q: 'How do I assign tasks to team members?',
          a: 'Open a task (click it or click edit icon), then enter the team member ID in the "Assign To" field. The task will show an "Assigned" badge and you can filter tasks by assignee.'
        },
        {
          q: 'What are task priorities?',
          a: 'Tasks have 4 priority levels: Low (can wait), Medium (normal priority), High (important), and Critical (urgent/blocking). Priority is shown with color-coded badges on each task card.'
        },
        {
          q: 'Can I add custom tags to tasks?',
          a: 'Yes! In the task modal, use the Tags field to add comma-separated tags like "frontend, urgent, bug". Tags help organize and filter tasks.'
        },
        {
          q: 'How do I track time on tasks?',
          a: 'Each task has "Estimated Hours" (how long you think it will take) and "Actual Hours" (time spent). Update these fields in the task modal to track time and compare estimates vs actuals.'
        }
      ]
    },
    {
      category: 'Collaboration',
      questions: [
        {
          q: 'Are updates real-time?',
          a: 'Yes! All task updates, status changes, and new tasks are broadcast in real-time via WebSocket. When one team member updates a task, everyone sees the change instantly without refreshing.'
        },
        {
          q: 'Can multiple people work on the same project?',
          a: 'Absolutely! Projects support unlimited team members. Everyone can view tasks, make updates, and collaborate in real-time. Use the assignment feature to coordinate who works on what.'
        },
        {
          q: 'How do I see who created or assigned a task?',
          a: 'Task metadata tracks who created each task and who assigned it (if applicable). This information is stored in the database and can be viewed in task details.'
        }
      ]
    },
    {
      category: 'Best Practices',
      questions: [
        {
          q: 'What\'s the best workflow for a new project?',
          a: '1) Create project with detailed description, 2) Click "Show Action Plan" to get AI guidance, 3) Click "AI Generate Tasks" to create initial task list, 4) Review and edit tasks as needed, 5) Assign tasks to team members, 6) Start moving tasks through the Kanban board, 7) Monitor progress on dashboard.'
        },
        {
          q: 'How should I organize tasks?',
          a: 'Use Categories (development, design, testing, documentation) to group related work. Use Tags for cross-cutting concerns (urgent, bug, enhancement). Set Priorities based on business impact. Use Dependencies (depends_on, blocks) for task ordering.'
        },
        {
          q: 'When should I use AI vs manual task creation?',
          a: 'Use AI for: kickstarting new projects, generating initial task lists, getting ideas when stuck. Use manual for: specific technical tasks, tasks requiring domain expertise, follow-up tasks, bug fixes. Best approach: AI for structure, manual for specifics.'
        },
        {
          q: 'How do I handle blocked tasks?',
          a: 'Drag tasks to the "Blocked" column when they cannot proceed. Add a comment in the description explaining the blocker. Use the Dependencies fields to link blocking tasks. Review blocked tasks regularly in team meetings.'
        }
      ]
    },
    {
      category: 'Technical',
      questions: [
        {
          q: 'What browsers are supported?',
          a: 'The application works best on modern browsers: Chrome, Firefox, Safari, and Edge (latest versions). We recommend Chrome for the best experience with drag-and-drop features.'
        },
        {
          q: 'Is my data secure?',
          a: 'Yes! All data is stored securely in a database with proper authentication. API requests use token-based authentication. Business case documents are encrypted and stored securely.'
        },
        {
          q: 'Can I export my project data?',
          a: 'Yes! Use the "Download Business Case" button to export project documents. Additional export features for tasks and reports are available through the API.'
        },
        {
          q: 'What AI model powers the features?',
          a: 'We use Google\'s Gemini 2.0 Flash model for AI features. It provides fast, accurate analysis and generation while maintaining cost efficiency.'
        }
      ]
    }
  ];

  const quickStart = [
    {
      step: 1,
      title: 'Create Your First Project',
      description: 'Start with the basics',
      action: 'Go to Projects',
      onClick: () => navigate('/projects')
    },
    {
      step: 2,
      title: 'Get AI Guidance',
      description: 'Let AI analyze your project',
      action: 'View Action Plan',
      onClick: null
    },
    {
      step: 3,
      title: 'Generate Tasks',
      description: 'AI creates your task list',
      action: 'Try AI Tasks',
      onClick: null
    },
    {
      step: 4,
      title: 'Start Working',
      description: 'Drag tasks through the board',
      action: 'Open Kanban',
      onClick: null
    }
  ];

  const toggleFaq = (category, index) => {
    const key = `${category}-${index}`;
    setOpenFaq(openFaq === key ? null : key);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center">
          <RocketLaunchIcon className="w-16 h-16 text-blue-600" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900">
          Welcome to Your AI-Powered Project Management System
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Everything you need to plan, execute, and deliver successful projects with AI assistance
        </p>
      </div>

      {/* Quick Start */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <LightBulbIcon className="w-6 h-6 text-yellow-500" />
          Quick Start Guide
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {quickStart.map((item) => (
            <div key={item.step} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">{item.description}</p>
              {item.onClick && (
                <button
                  onClick={item.onClick}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  {item.action}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Core Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            const colorClasses = {
              blue: 'bg-blue-100 text-blue-600',
              purple: 'bg-purple-100 text-purple-600',
              green: 'bg-green-100 text-green-600',
              orange: 'bg-orange-100 text-orange-600',
              indigo: 'bg-indigo-100 text-indigo-600'
            };
            
            return (
              <div key={idx} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 ${colorClasses[feature.color]} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="space-y-2">
                  {feature.steps.map((step, stepIdx) => (
                    <div key={stepIdx} className="flex items-start gap-2 text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <div className="text-center mb-8">
          <QuestionMarkCircleIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600">
            Everything you need to know about using the platform
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {faqs.map((category) => (
            <div key={category.category} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">{category.category}</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {category.questions.map((faq, idx) => {
                  const isOpen = openFaq === `${category.category}-${idx}`;
                  return (
                    <div key={idx}>
                      <button
                        onClick={() => toggleFaq(category.category, idx)}
                        className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                        <ArrowRightIcon
                          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4 text-gray-700 bg-gray-50">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FlagIcon className="w-6 h-6 text-blue-600" />
          Pro Tips
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex gap-3">
            <ClockIcon className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Save Time with AI</h4>
              <p className="text-gray-700 text-sm">
                Use "AI Generate Tasks" to create your initial task list in seconds, then customize as needed.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <SparklesIcon className="w-6 h-6 text-purple-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Get Smart Guidance</h4>
              <p className="text-gray-700 text-sm">
                Check the AI Action Plan regularly for phase-specific recommendations and risk alerts.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <ListBulletIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Stay Organized</h4>
              <p className="text-gray-700 text-sm">
                Use categories, tags, and priorities to keep tasks organized and easy to filter.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <UserGroupIcon className="w-6 h-6 text-orange-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Collaborate Effectively</h4>
              <p className="text-gray-700 text-sm">
                Assign tasks to team members and leverage real-time updates for seamless collaboration.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-12 text-white">
        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p className="text-xl mb-8 opacity-90">
          Create your first project and experience AI-powered project management
        </p>
        <button
          onClick={() => navigate('/projects')}
          className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
        >
          Create Your First Project
          <ArrowRightIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
